import merge from 'lodash/merge';
import type { App, Dictionary, AppLoaderConfig, CustomElementApp, AuthConfig, AppConfig } from '@nab/x-types';

/**
 * Tracks app instances as they're loaded
 */
let registeredApps: Dictionary<AppLoaderConfig> = {};
let instances: Dictionary<AppLoaderConfig> = {};

/**
 * The method used to retrieve the cached metadata for the mini-app using its instance Id.
 * @param instanceId The instance unique ID that is used to cache the configuration of the app.
 * For apps created using generator v6.x.x you can use the tag-name of the web component.
 */
export function getAppMetaDataForInstanceId(instanceId: string): App {
    return (instances[instanceId] && instances[instanceId].appMetaData) || undefined;
}

/**
 * The method used to retrieve the authConfig for the miniApp using its instance Id.
 * @param instanceId The instance unique ID that is used to cache the configuration of the app.
 * For apps created using generator v6.x.x you can use the tag-name of the web component.
 */
export function getAuthConfigForInstanceId(instanceId: string): AuthConfig {
    return (instances[instanceId] && instances[instanceId].authConfig) || undefined;
}

export function getAppConfigForInstanceId(instanceId: string): AppConfig {
    return (instances[instanceId] && instances[instanceId].appConfig) || undefined;
}

export function getRegisteredApp<APP extends App = App>(id: string): AppLoaderConfig<APP> {
    return <AppLoaderConfig<APP>>registeredApps[id];
}

export function getLoaderConfigForInstanceId(instanceId: string): AppLoaderConfig {
    return instances[instanceId];
}

/**
 * Registers and caches the instance of the mini-app's appLoaderConfig.
 * @param instanceId The instance unique ID that will be used to cache the configuration of the app.
 * @param appLoaderConfig The config that will be cached under the instanceId name
 */
export function registerInstance(instanceId: string, appLoaderConfig: AppLoaderConfig<CustomElementApp>) {
    const {
        appMetaData: { id, tag }
    } = appLoaderConfig;
    registeredApps[id] = appLoaderConfig;
    instances[instanceId] = appLoaderConfig;

    /**
     * added to support backward compatibility to older apps that used to say the config cache as tag name
     */
    if (tag) {
        instances[tag] = appLoaderConfig;
    }
}

/**
 * Update of all miniapps' appLoaderConfig
 * @param data The config that will be merged to configuration of all apps.
 */
export function updateAllInstances(data: Partial<AppLoaderConfig<CustomElementApp>>) {
    const keys = Object.keys(instances);
    keys.forEach((key) => {
        instances[key] = merge({}, instances[key], data);
        const { id } = instances[key].appMetaData;
        registeredApps[id] = instances[key];
    });
}

/**
 * Resets the cached configurations.
 */
export function reset() {
    registeredApps = {};
    instances = {};
}
